import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, loadSpecificData, sortArrayOfObjects } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import EditStudentData from '../modals/edit_student';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';

const buildStudentDraft = (student_info = {}) => ({
	student_id: student_info.student_id,
	student_code: student_info.student_code || '',
	last_name: student_info.last_name || '',
	first_name: student_info.first_name || '',
	grade: student_info.grade ? `${student_info.grade}` : '',
	homeroom_teacher_id: student_info.homeroom_teacher_id ? `${student_info.homeroom_teacher_id}` : '',
});

const mergeSavedStudentIntoList = (student_list = [], old_student_id, saved_student) => {
	const old_student_id_string = `${old_student_id}`;
	const new_student_id_string = `${saved_student.student_id}`;
	const original_student_index = student_list.findIndex((student_info) => `${student_info.student_id}` === old_student_id_string);

	if(old_student_id_string === new_student_id_string)
	{
		return student_list.map((student_info) => (
			`${student_info.student_id}` === old_student_id_string ? saved_student : student_info
		));
	}

	const next_student_list = student_list.filter((student_info) => (
		`${student_info.student_id}` !== old_student_id_string && `${student_info.student_id}` !== new_student_id_string
	));

	if(original_student_index === -1) return [...next_student_list, saved_student];

	const safe_insert_index = Math.min(original_student_index, next_student_list.length);
	return [
		...next_student_list.slice(0, safe_insert_index),
		saved_student,
		...next_student_list.slice(safe_insert_index),
	];
};

const studentsMatch = (student_a = {}, student_b = {}) => {
	const normalized_a = buildStudentDraft(student_a);
	const normalized_b = buildStudentDraft(student_b);

	return normalized_a.student_code.trim() === normalized_b.student_code.trim()
		&& normalized_a.last_name.trim() === normalized_b.last_name.trim()
		&& normalized_a.first_name.trim() === normalized_b.first_name.trim()
		&& `${normalized_a.grade}` === `${normalized_b.grade}`
		&& `${normalized_a.homeroom_teacher_id}` === `${normalized_b.homeroom_teacher_id}`;
};

export default function DatabaseDataStudents({ schoolInfo, databaseData, setDatabaseData, scheduleInfo, courses, teachers, dataIsLoading, blocks, pulloutGroups, setPulloutGroups, handlePullNewData }) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [displayResults, setDisplayResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);
	const [maxStudentsToShow, setMaxStudentsToShow] = useState(50);
	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const studentCodeInputRef = useRef(null);
	const studentLastNameInputRef = useRef(null);
	const studentFirstNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	
	const handleIsSearching = (e) => {
		const search_value = e.target.value;
		
		if(search_value.length > 1)
		{
			if(e.keyCode !== 8)
			{
				setIsLoading(false);
				setIsSearching(true);
			}
			else
			{
				setIsLoading(true);
				setIsSearching(false);
			}
		}
		else
		{
			setIsLoading(false);
			setIsSearching(false);
		}
	};

	const teacherOptions = teachers.reduce((results, teacher_info) => {
		const teacher_id = `${teacher_info.teacher_id}`;
		const teacher_full_name = teacher_info.first_name ? `${teacher_info.name}, ${teacher_info.first_name}` : teacher_info.name;
		results.push({value: teacher_id, display: teacher_full_name});
		return results;
	}, []);

	const gradeOptions = useMemo(() => sortArrayOfObjects(
		[...new Set(databaseData.reduce((results, student_info) => {
			const grade = student_info?.grade ? `${student_info.grade}` : '';
			if(grade) results.push(grade);
			return results;
		}, []))].map((grade) => ({value: grade, display: grade === '0' ? 'K' : grade})),
		'value',
		'number',
		'asc'
	), [databaseData]);
	const schoolDataFilterCategories = useMemo(() => ([
		{
			key: 'grade',
			title: 'Grade',
			dataKey: 'grade',
			options: gradeOptions,
		},
	]), [gradeOptions]);
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['last_name', 'first_name', 'student_code']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}

		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [dataIsLoading]);
	
	useEffect(() => {
		setMaxStudentsToShow(50);
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const isEditingField = useCallback((student_id, field) => (
		editingCell
		&& `${editingCell.student_id}` === `${student_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getStudentByID = useCallback((student_id) => (
		databaseData.find((student_info) => `${student_info.student_id}` === `${student_id}`) || null
	), [databaseData]);

	const startCellEdit = useCallback((student_info, field) => {
		if(!student_info) return;

		setEditingCell({student_id: student_info.student_id, field});
		setCellDraft(buildStudentDraft(student_info));
		setIsSavingCell(false);
	}, []);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			student_code: studentCodeInputRef,
			last_name: studentLastNameInputRef,
			first_name: studentFirstNameInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const getHomeroomTeacherLastName = (teacher_id) => {
		const homeroom_teacher = teachers.find((teacher_info) => `${teacher_info.teacher_id}` === `${teacher_id}`);
		return homeroom_teacher ? homeroom_teacher.name : '';
	};

	const handleSaveCell = useCallback(async (student_info, draft_override = null) => {
		const draft_to_save = draft_override || cellDraft;
		if(!draft_to_save || !editingCell || isSavingCell) return;

		const trimmed_student_code = draft_to_save.student_code ? draft_to_save.student_code.trim() : '';
		const trimmed_last_name = draft_to_save.last_name ? draft_to_save.last_name.trim() : '';
		const trimmed_first_name = draft_to_save.first_name ? draft_to_save.first_name.trim() : '';
		const next_grade = draft_to_save.grade ? `${draft_to_save.grade}` : '';
		const next_homeroom_teacher_id = draft_to_save.homeroom_teacher_id ? `${draft_to_save.homeroom_teacher_id}` : '';
		const draft_to_compare = {
			...student_info,
			student_code: trimmed_student_code,
			last_name: trimmed_last_name,
			first_name: trimmed_first_name,
			grade: next_grade,
			homeroom_teacher_id: next_homeroom_teacher_id,
		};

		if(studentsMatch(student_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_student_code || !trimmed_last_name || !trimmed_first_name || !next_grade)
		{
			swal({
				title: 'Missing Data',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div className='text-align-left'>
						<p>You are missing data! Make sure you have the following data before saving:</p>
						<ul>
							<li>Student Code</li>
							<li>First Name</li>
							<li>Last Name</li>
							<li>Gender</li>
							<li>Grade</li>
						</ul>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);
		if(draft_override) setCellDraft(draft_override);

		const original_student_id = student_info.student_id;
		const full_student = await loadSpecificData({
			data_type:'student',
			data_id:original_student_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(!full_student || full_student.msg)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t load this student&apos;s latest data. Please try again.</p></div>
			});
			return;
		}

		const student_to_save = {
			...full_student,
			student_id: original_student_id,
			student_code: trimmed_student_code,
			last_name: trimmed_last_name,
			first_name: trimmed_first_name,
			grade: next_grade,
			homeroom_teacher_id: next_homeroom_teacher_id || null,
		};

		const response_data = await saveDataToDatabase({
			school_id:schoolInfo.school_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			data_type:'student',
			data_to_save:student_to_save,
			database:'elementary'
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t save this student value. Please try again.</p></div>
			});
			return;
		}

		const saved_student_id = response_data.new_data_id;
		let saved_student = {
			...student_info,
			...student_to_save,
			student_id: saved_student_id,
		};

		const refreshed_student = await loadSpecificData({
			data_type:'student',
			data_id:saved_student_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(refreshed_student && !refreshed_student.msg) saved_student = refreshed_student;

		const next_database_data = mergeSavedStudentIntoList(databaseData, original_student_id, saved_student);
		const next_filtered_results = mergeSavedStudentIntoList(filteredResults, original_student_id, saved_student);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, cancelCellEdit]);

	const handleCellBlur = (student_info, field) => {
		if(!isEditingField(student_info.student_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(student_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, student_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(student_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleStudentDraftFieldChange = (field, event) => {
		const { value } = event.target;
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const handleSingleSelect = (student_info, field, value) => {
		const next_draft = {...(cellDraft || buildStudentDraft(student_info)), [field]: `${value}`};
		handleSaveCell(student_info, next_draft);
	};

	useEffect(() => {
		if(!editingCell || !['grade', 'homeroom_teacher_id'].includes(editingCell.field)) return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const student_info = getStudentByID(editingCell.student_id);
			if(!student_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(student_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getStudentByID, cancelCellEdit]);
	const showMoreStudents = () => {
		const num_students_to_show = maxStudentsToShow + 50;
		setMaxStudentsToShow(num_students_to_show);
	};
	const handleSortClick = (sortedData) => {
		cancelCellEdit();
		setDisplayResults([...sortedData]);
	};

	const handleFilterClick = (selectedFilters = {}) => {
		cancelCellEdit();
		const next_filtered_results = applySchoolDataFilters(databaseData, selectedFilters, schoolDataFilterCategories);
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		setFilteredResults([...next_filtered_results]);
		setHandleChangeComplete(true);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};
	
	const handeDeleteData = async (data_id) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'student', data_ids:[data_id], database:'elementary'};
		let delete_data_bool = true;
		
		const data_index = databaseData.findIndex((student) => `${student.student_id}` === `${data_id}`);
		const filtered_results_index = filteredResults.findIndex((student) => `${student.student_id}` === `${data_id}`);
		const data_info = databaseData[data_index];
		
		if(!dontShowDeletePopup)
		{
			const gender_pronoun = (data_info.gender === 'M') ? 'him' : ((data_info.gender === 'F') ? 'her' : 'them');
			const options =  {
				title: 'Are you sure?',
				icon: 'warning',
				dangerMode: true,
				buttons: {
					cancel: {
						text: 'Cancel',
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: 'Yes',
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete {data_info.first_name} {data_info.last_name}?</p>
							<p className='red-text'>This will <strong>permanently</strong> remove {gender_pronoun} from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			databaseData.splice(data_index, 1);
			filteredResults.splice(filtered_results_index, 1);
			setDatabaseData([...databaseData]);
			setFilteredResults([...filteredResults]);
			
			deleteData(data);
		}
	};
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);

		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 0) handleChange();
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(!handleChangeComplete || !filterActive) setFilteredResults([...databaseData]);
	}, [databaseData, dataIsLoading, filterActive, handleChangeComplete]);
	
	const visibleResults = displayResults.slice(0, maxStudentsToShow);
	const row_style = {gridTemplateColumns:'100px 2fr 2fr 80px 2fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditStudentData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} teachers={teachers} students={databaseData} courses={courses} setDatabaseData={setDatabaseData} pulloutGroups={pulloutGroups} setPulloutGroups={setPulloutGroups}/>
		}
		{showUploadModal &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Students' uploadAPIName='studentdata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Students</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder='Search for students...' value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
				</div>
				<div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='student_code' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Last Name <SortToggle handleOnClick={handleSortClick} sortKey='last_name' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>First Name <SortToggle handleOnClick={handleSortClick} sortKey='first_name' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Grade <SortToggle handleOnClick={handleSortClick} sortKey='grade' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Homeroom <SortToggle handleOnClick={handleSortClick} sortKey='homeroom_teacher_id' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div>
				</div>
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{visibleResults.map((data_info) => {
						const display_grade = (data_info.grade === '0') ? 'K' : data_info.grade;
						const homeroom_teacher_last_name = getHomeroomTeacherLastName(data_info.homeroom_teacher_id);
						
						return (
							<div className='school-data-database-row' style={row_style} key={data_info.student_id}>
								<div
									ref={isEditingField(data_info.student_id, 'student_code') ? activeEditorRef : null}
									className={isEditingField(data_info.student_id, 'student_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'student_code') : undefined}
								>
									{isEditingField(data_info.student_id, 'student_code') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
												<input
													ref={studentCodeInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.student_code || ''}
													disabled={isSavingCell}
													onChange={(e) => handleStudentDraftFieldChange('student_code', e)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'student_code')}
												/>
										)
									:
										<div className='medium-text'>{data_info.student_code}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.student_id, 'last_name') ? activeEditorRef : null}
									className={isEditingField(data_info.student_id, 'last_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'last_name') : undefined}
								>
									{isEditingField(data_info.student_id, 'last_name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
												<input
													ref={studentLastNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.last_name || ''}
													disabled={isSavingCell}
													onChange={(e) => handleStudentDraftFieldChange('last_name', e)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'last_name')}
												/>
										)
									:
										<div>{data_info.last_name}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.student_id, 'first_name') ? activeEditorRef : null}
									className={isEditingField(data_info.student_id, 'first_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'first_name') : undefined}
								>
									{isEditingField(data_info.student_id, 'first_name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
												<input
													ref={studentFirstNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.first_name || ''}
													disabled={isSavingCell}
													onChange={(e) => handleStudentDraftFieldChange('first_name', e)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'first_name')}
												/>
										)
									:
										<div>{data_info.first_name}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.student_id, 'grade') ? activeEditorRef : null}
									className={isEditingField(data_info.student_id, 'grade') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'grade') : undefined}
								>
									{isEditingField(data_info.student_id, 'grade') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<div className='school-data-inline-select-popover'>
												{gradeOptions.map((grade_option) => {
													const grade_selected = `${cellDraft?.grade || ''}` === `${grade_option.value}`;

													return (
														<button key={grade_option.value} type='button' className={`school-data-inline-select-option ${grade_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleSingleSelect(data_info, 'grade', grade_option.value)}>
															{grade_option.display}
														</button>
													);
												})}
											</div>
										)
									:
										<div className='medium-text'>{display_grade}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.student_id, 'homeroom_teacher_id') ? activeEditorRef : null}
									className={isEditingField(data_info.student_id, 'homeroom_teacher_id') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'homeroom_teacher_id') : undefined}
								>
									{isEditingField(data_info.student_id, 'homeroom_teacher_id') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<div className='school-data-inline-select-popover'>
												<button type='button' className={`school-data-inline-select-option school-data-inline-select-option-none ${`${cellDraft?.homeroom_teacher_id || ''}` === '' ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleSingleSelect(data_info, 'homeroom_teacher_id', '')}>
													<span className='school-data-inline-select-option-none-label'>None</span>
													<span className='school-data-inline-select-option-none-helper'>No homeroom teacher</span>
												</button>
												{teacherOptions.map((teacher_option) => {
													const teacher_selected = `${cellDraft?.homeroom_teacher_id || ''}` === `${teacher_option.value}`;

													return (
														<button key={teacher_option.value} type='button' className={`school-data-inline-select-option ${teacher_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleSingleSelect(data_info, 'homeroom_teacher_id', teacher_option.value)}>
															{teacher_option.display}
														</button>
													);
												})}
											</div>
										)
									:
										<div className='medium-text'>{homeroom_teacher_last_name}</div>
									}
								</div>
								<div className='school-data-database-row-btn-container'>
									<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(data_info.student_id)}/>
									<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData(data_info.student_id)}/>
								</div>
							</div>
						);
					})}
					</>
				)}
			</div>
			{(displayResults.length > maxStudentsToShow) &&
				<div className='btn blue-btn load-more-btn' onClick={showMoreStudents}>Load More Students</div>
			}
		</div>
		</>
	);
}
